import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListChecks } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SyncedLocationsList } from '@/components/synced-locations/SyncedLocationsList';
import { SyncedLocationFilters } from '@/components/synced-locations/SyncedLocationFilters';
import { FloatingActionBar } from '@/components/synced-locations/FloatingActionBar';
import { LocationSelectionProvider, useLocationSelection } from '@/contexts/LocationSelectionContext';
import { buildListQuery, useSyncedLocations, useLocationStats, useSyncedLocationFacets } from '@/api/synced-locations';
import { paramsFromSearch } from '@/lib/synced-location-filters';
import type { FacetGroup, SyncedLocationListParams } from '@/types/api';

/**
 * The facet groups counted on every filter change: one indexed GROUP BY each.
 * The rest — the taxonomy terms (a join over 1.5M term rows), the missing
 * fields (seven counts) and the contact kinds — are counted only when their
 * menu is opened, because on 178,000 rows they cost seconds each and a
 * keystroke in the search box must not wait for them.
 */
const LIGHT_FACET_GROUPS: readonly FacetGroup[] = ['source', 'assetClass', 'country', 'city', 'images', 'capacity'];
const HEAVY_FACET_GROUPS: readonly FacetGroup[] = ['category', 'tag', 'amenity', 'missing', 'contact', 'locationType'];

function SyncedLocationsContent(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the filter state: every control writes to it, so a filtered
  // view survives a reload, a back button and a pasted link.
  const filters = useMemo(() => paramsFromSearch(searchParams), [searchParams]);

  const { data: locationsData, isLoading, error, refetch } = useSyncedLocations(filters);
  const { data: lightFacets } = useSyncedLocationFacets(filters, LIGHT_FACET_GROUPS);
  const [wantedHeavy, setWantedHeavy] = useState<FacetGroup[]>([]);
  const { data: heavyFacets } = useSyncedLocationFacets(filters, wantedHeavy, { enabled: wantedHeavy.length > 0 });
  const facets = useMemo(() => (lightFacets ? { ...heavyFacets, ...lightFacets } : undefined), [lightFacets, heavyFacets]);
  const requestFacet = useCallback((group: FacetGroup) => {
    if (HEAVY_FACET_GROUPS.includes(group)) setWantedHeavy((prev) => (prev.includes(group) ? prev : [...prev, group]));
  }, []);
  const singleAccount = filters.partnerAccountIds?.length === 1 ? filters.partnerAccountIds[0] : undefined;
  const { data: stats } = useLocationStats(singleAccount);
  const {
    isSelectionMode,
    selectedIds,
    toggleSelectionMode,
    toggle,
    selectAll,
    deselectAll,
    clearSelection,
  } = useLocationSelection();

  const replaceFilters = useCallback(
    (next: SyncedLocationListParams) => {
      setSearchParams(new URLSearchParams(buildListQuery(next)));
    },
    [setSearchParams]
  );

  // A change to any filter starts again at page 1: page 7 of the old result
  // is not a page of the new one.
  const handleFilterChange = useCallback(
    (change: Partial<SyncedLocationListParams>) => {
      const { page: _page, ...rest } = filters;
      replaceFilters({ ...rest, ...change });
    },
    [filters, replaceFilters]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      replaceFilters(page === 1 ? (({ page: _page, ...rest }) => rest)(filters) : { ...filters, page });
    },
    [filters, replaceFilters]
  );

  const handleOperationCreated = () => {
    void refetch();
  };

  const locations = locationsData?.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Synced Locations</h1>
          <p className="text-muted-foreground">
            View and manage locations synced from your partner accounts.
          </p>
        </div>

        {/* Selection mode toggle */}
        <Button
          variant={isSelectionMode ? 'default' : 'outline'}
          size="sm"
          onClick={toggleSelectionMode}
          data-testid="select-mode-toggle"
        >
          <ListChecks className="mr-1.5 h-4 w-4" />
          {isSelectionMode ? 'Exit Selection' : 'Select'}
        </Button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Locations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pending Enrichment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.byEnrichmentStatus.pending ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Enriched</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.byEnrichmentStatus.enriched ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Published</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.byPublicationStatus.published ?? 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <SyncedLocationFilters
        filters={filters}
        facets={facets}
        onRequestFacet={requestFacet}
        onFilterChange={handleFilterChange}
        onReplaceFilters={replaceFilters}
      />

      {/* Error state */}
      {error ? (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load synced locations. Please try again.
          </p>
        </div>
      ) : (
        <SyncedLocationsList
          locations={locations}
          pagination={locationsData?.pagination}
          isLoading={isLoading}
          onPageChange={handlePageChange}
          isSelectionMode={isSelectionMode}
          selectedIds={selectedIds}
          onToggleSelection={toggle}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
        />
      )}

      {/* Floating action bar for bulk operations */}
      {isSelectionMode && (
        <FloatingActionBar
          selectedIds={selectedIds}
          locations={locations}
          onClearSelection={clearSelection}
          onOperationCreated={handleOperationCreated}
        />
      )}
    </div>
  );
}

export function SyncedLocationsPage(): JSX.Element {
  return (
    <LocationSelectionProvider>
      <SyncedLocationsContent />
    </LocationSelectionProvider>
  );
}
